import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { dealApi, dealQueryKeys } from '@crm/entities/deal';
import { dealsBoardQueryKeys } from '@crm/entities/deals-board';
import { toastManager } from '@crm/entities/toast';
import { getApiErrorMessage } from '@crm/shared/api';

import type { TPostponeDealFormState } from '../model';
import type { TDeal } from '@crm/entities/deal';

export const usePostponeDealMutation = ({ dealIds }: { dealIds: readonly TDeal['id'][] }) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const invalidateRelatedQueries = async () => {
        const queryKeys = [
            ...dealIds.map(dealQueryKeys.detail),
            dealQueryKeys.postponedDeals.all(),
            dealQueryKeys.lostDeals.all(),
            dealQueryKeys.inWorkOrFinishedDeals.all(),
        ];

        await Promise.all([
            ...queryKeys.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
            queryClient.invalidateQueries({
                queryKey: dealsBoardQueryKeys.board(),
                exact: true,
                refetchType: 'all',
            }),
        ]);
    };

    const { mutateAsync: postponeDealMutationFn, isPending } = useMutation({
        mutationKey: ['deal', 'postponed', ...dealIds],
        mutationFn: (data: TPostponeDealFormState) => {
            if (dealIds.length === 0 || new Set(dealIds).size !== dealIds.length) {
                throw new Error(t('crm.common.deal.errors.invalidIds'));
            }

            return dealApi.postponeDeals({
                dealIds,
                payload: data,
            });
        },
        onSuccess: async () => {
            await invalidateRelatedQueries();

            toastManager.onSuccess({
                title:
                    dealIds.length > 1
                        ? t('crm.postponeDeal.notifications.success.batch', {
                              count: dealIds.length,
                          })
                        : t('crm.postponeDeal.notifications.success.single'),
            });
        },
        onError: (error) => {
            toastManager.onError({
                title: getApiErrorMessage(error, t('crm.postponeDeal.errors.submit')),
            });
        },
    });

    return {
        postponeDealMutationFn,
        isPending,
    };
};
